import { useState, useEffect, useRef } from 'react';
import { features } from './constants';
import featureImage from '../../../assets/images/featureSkill.jpg';

export default function Features() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} id="features" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-6">
        <div className={`max-w-4xl mx-auto text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl md:text-4xl font-bold text-[#2a4b7c] mb-6">
            Các tính năng cốt lõi
          </h2>
          <p className="text-lg text-slate-600">
            7 module tích hợp hoàn chỉnh cho hệ sinh thái quản trị năng lực
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Feature List */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`p-6 rounded-xl cursor-pointer ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                } ${
                  activeFeature === index 
                    ? 'bg-gradient-to-r from-[#2a4b7c] to-[#3d5a80] text-white shadow-lg' 
                    : 'bg-white hover:bg-slate-50'
                }`}
                style={{ 
                  transitionDelay: isVisible ? `${200 + index * 100}ms` : '0ms',
                  transition: isVisible 
                    ? 'opacity 1000ms ease-out, transform 1000ms ease-out, background-color 300ms ease-out, color 300ms ease-out' 
                    : 'opacity 1000ms ease-out, transform 1000ms ease-out'
                }}
                onClick={() => setActiveFeature(index)}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    activeFeature === index ? 'bg-white/20' : 'bg-gradient-to-r from-blue-50 to-cyan-50'
                  }`}>
                    <i className={`${feature.icon} text-xl ${
                      activeFeature === index ? 'text-white' : 'text-[#2a4b7c]'
                    }`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1">{feature.title}</h3>
                    <p className={`text-sm ${
                      activeFeature === index ? 'text-blue-100' : 'text-slate-600'
                    }`}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Feature Detail */}
          <div className={`bg-white p-8 rounded-2xl shadow-sm border transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            <div className="mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-[#2a4b7c] to-[#3d5a80] rounded-xl flex items-center justify-center mb-4 hover:scale-110 transition-transform duration-300">
                <i className={`${features[activeFeature].icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-2xl font-bold text-[#2a4b7c] mb-4">
                {features[activeFeature].title}
              </h3>
              
              {/* Feature Details */}
              <div className="space-y-3 mb-6">
                {features[activeFeature].features.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-start space-x-3 transition-all duration-500"
                    style={{ transitionDelay: `${idx * 50}ms` }}
                  >
                    <div className="w-2 h-2 bg-[#2a4b7c] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>

              {/* Conclusion */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-[#2a4b7c] hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center space-x-2">
                  <i className="ri-arrow-right-line text-[#2a4b7c] font-bold"></i>
                  <p className="font-bold text-[#2a4b7c] italic">
                    {features[activeFeature].conclusion}
                  </p>
                </div>
              </div>
            </div>
            
            <div 
              className="w-full h-80 bg-gradient-to-r from-slate-100 to-blue-100 rounded-xl bg-cover bg-center hover:scale-[1.02] transition-transform duration-500"
              style={{
                backgroundImage: `url(${featureImage})`
              }}
            ></div>
          </div>
        </div>
      </div>
    </section>
  );
}
